import React, { useState } from "react";
import { Button, Box, CircularProgress, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { getAccessToken } from "../../../utils/tokenManager";
import { getAuthToken } from "../../../utils/getAuthToken";
import { fetchFacebookAuthorizationUrl } from "../../../services/facebookApi";

/**
 * Reusable "Connect Facebook" action. Fetches the authorization URL from the
 * backend and redirects the browser — it never builds the OAuth URL itself.
 */
const ConnectFacebookButton = ({ label = "Connect", sx }) => {
  const workspaceId = useSelector(
    (s) => s.workspace?.currentWorkspace?._id || s.auth?.user?.workspaceId
  );

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleConnect = async () => {
    setErrorMessage("");

    if (!workspaceId) {
      setErrorMessage("No workspace selected.");
      return;
    }

    setLoading(true);
    try {
      let token = getAccessToken();
      if (!token) token = await getAuthToken();
      if (!token) {
        setErrorMessage("Not authenticated.");
        return;
      }

      const authorizationUrl = await fetchFacebookAuthorizationUrl(workspaceId, token);
      if (!authorizationUrl) {
        setErrorMessage("Could not start Facebook connection.");
        return;
      }

      window.location.href = authorizationUrl;
    } catch (err) {
      setErrorMessage(
        err.response?.data?.message || err.message || "Failed to start Facebook connection."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Button
        variant="outlined"
        size="small"
        disabled={loading}
        onClick={handleConnect}
        sx={{ textTransform: "none", width: "fit-content", ...sx }}
      >
        {loading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            Connecting <CircularProgress size={14} sx={{ color: "inherit" }} />
          </Box>
        ) : (
          label
        )}
      </Button>
      {errorMessage && (
        <Typography color="error" variant="caption">
          {errorMessage}
        </Typography>
      )}
    </Box>
  );
};

export default ConnectFacebookButton;
